import { Space } from "antd";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { ConductTableColumns } from "~pages/Conducts/ConductListPage/enums/conductTableColumns.ts";
import { useGetConductTableColumns } from "~pages/Conducts/ConductListPage/hooks/useGetConductTableColumns.tsx";
import { useGetConductTableFilters } from "~pages/Conducts/ConductListPage/hooks/useGetConductTableFilters.tsx";
import ConductUpsertModal from "~pages/Conducts/ConductUpsertModal/ConductUpsertModal.tsx";
import { useConductService } from "~pages/Conducts/hooks/useConductService.ts";
import type { ConductTransformed } from "~pages/Conducts/types/conductTransformed.ts";
import { useFetchAllConducts } from "./hooks/useFetchAllConducts.ts";
import type { SearchFilterParams } from "./types/searchFilterParams.ts";

const CONDUCT_INCLUDES = ["competitions", "seasons", "persons", "fixtures", "entities"].join(",");

const ConductListPage = () => {
  const { formatMessage } = useIntl();
  const { sport } = useParams();
  const { hasPermission } = useUserPermissions();
  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.conducts.listPage;

  const columns = useGetConductTableColumns();
  const { filtersConfig, currentFilter } = useGetConductTableFilters();
  const paginationConfig = useHandlePagination(20);

  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [isAddConductModalOpen, setIsAddConductModalOpen] = useState(false);
  const [conductToUpdate, setConductToUpdate] = useState<ConductTransformed | null>(null);

  const params = useMemo<SearchFilterParams>(
    () => ({
      include: CONDUCT_INCLUDES,
      ...(currentFilter.competition && { competitionId: currentFilter.competition.value }),
      ...(currentFilter.season && { seasonId: currentFilter.season.value }),
      ...(currentFilter.status && { conductStatus: currentFilter.status }),
      ...(currentFilter.conductType?.length && { conductType: currentFilter.conductType.join(",") }),
      ...(searchText && { nameFullLocalContains: searchText }),
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      sortBy: sortBy || "",
    }),
    [currentFilter, searchText, paginationConfig.pageSize, paginationConfig.offset, sortBy],
  );

  const { data, isLoading, refetch } = useFetchAllConducts(params, sortBy !== null);
  const {
    upsertConduct,
    isUpsertPending: isUpsertConductInProcess,
    deleteConduct,
    isDeletePending: isDeleteConductInProcess,
  } = useConductService(params);

  const canUpdateConduct = hasPermission(PermissionNames.CONDUCT, PermissionOperations.UPDATE);

  return (
    <>
      <DataTable<ConductTransformed>
        title={formatMessage({ id: "conduct" })}
        columns={columns}
        dataSource={data?.data || []}
        rowKey="conductId"
        isDataLoading={isLoading || isUpsertConductInProcess || isDeleteConductInProcess}
        permissionNames={[PermissionNames.CONDUCT]}
        dataTableUiConfiguration={dataTableUiConfiguration}
        filtersConfig={filtersConfig}
        searchConfig={{ handleSearch: setSearchText, searchText: searchText }}
        searchPlaceholderIntlKey="conduct.search.person.name"
        handleAdd={() => setIsAddConductModalOpen(true)}
        addButtonIntlKey="conduct"
        handleRefresh={async () => {
          await refetch();
        }}
        rowActions={{
          custom: {
            update: {
              hidden: () => !canUpdateConduct,
              renderAction: (row) => (
                <Space onClick={() => setConductToUpdate(row)}>
                  <i className="fas fa-pen-to-square mr-2" />
                  <FormattedMessage id="conduct.update" />
                </Space>
              ),
            },
          },
          base: {
            delete: {
              onClick: (row) => deleteConduct(row.conductId),
              itemName: (row) => row.person?.nameFullLocal,
              loading: isDeleteConductInProcess,
            },
          },
        }}
        columnChooserLocalStorageKey={`${sport}.tables.settings.columns.conducts`}
        columnSortConfig={{
          sorterLocalStorageKey: `${sport}.tables.settings.sort.conducts`,
          defaultSorter: {
            columnKey: ConductTableColumns.Added,
            order: "descend",
          },
          onSortChange: setSortBy,
        }}
        paginationConfig={{ ...paginationConfig, hasNext: !!data?.nextLink }}
      />
      {isAddConductModalOpen && (
        <ConductUpsertModal
          onUpsert={upsertConduct}
          onClose={() => setIsAddConductModalOpen(false)}
          isUpsertInProcess={isUpsertConductInProcess}
        />
      )}
      {conductToUpdate && (
        <ConductUpsertModal
          conduct={conductToUpdate}
          onUpsert={upsertConduct}
          onClose={() => setConductToUpdate(null)}
          isUpsertInProcess={isUpsertConductInProcess}
        />
      )}
    </>
  );
};

export default ConductListPage;
